import { z } from 'zod'
import { pageQuerySchema } from '../pagination'
import { idSchema, text } from '../validators'
import { QUESTION_STATUSES } from '../vocab'

/**
 * Question threads (P-ENG-001, GAP-DD-018, GAP-API-001): an investor asks, a
 * manager replies, the investor follows up. Messages are append-only and never
 * overwritten. Visible to the author and the org's investor_admin (P-ID-007).
 */

export const QUESTION_BODY_MAX = 4000
export const QUESTION_SUBJECT_MAX = 160

export const questionCreateSchema = z.strictObject({
  engagement_id: idSchema,
  subject: text(3, QUESTION_SUBJECT_MAX).optional(),
  body: text(8, QUESTION_BODY_MAX),
})
export type QuestionCreate = z.infer<typeof questionCreateSchema>

/** A manager reply or an investor follow-up: non-empty text, never blank (GAP-API-001). */
export const questionMessageCreateSchema = z.strictObject({
  body: text(1, QUESTION_BODY_MAX),
})
export type QuestionMessageCreate = z.infer<typeof questionMessageCreateSchema>

export const questionListQuerySchema = pageQuerySchema.extend({
  engagement_id: idSchema.optional(),
  status: z.enum(QUESTION_STATUSES.values).optional(),
})
export type QuestionListQuery = z.infer<typeof questionListQuerySchema>

const engagementRefSchema = z.object({
  id: z.string(),
  title: z.string(),
  issuer_name: z.string(),
})

/** Investor allowlist: display names, no user ids (P-ID-006). */
export const investorQuestionMessageSchema = z.object({
  id: z.string(),
  author_name: z.string(),
  author_role: z.enum(['investor', 'manager']),
  mine: z.boolean(),
  body: z.string(),
  created_at: z.string(),
})
export type InvestorQuestionMessage = z.infer<typeof investorQuestionMessageSchema>

export const investorQuestionSchema = z.object({
  id: z.string(),
  subject: z.string(),
  status: z.enum(QUESTION_STATUSES.values),
  /** Null once the engagement is no longer visible to the caller (revoked subscription). */
  engagement: engagementRefSchema.nullable(),
  asked_by_name: z.string(),
  asked_by_me: z.boolean(),
  message_count: z.number().int(),
  last_message_at: z.string(),
  created_at: z.string(),
  can_follow_up: z.boolean(),
  messages: z.array(investorQuestionMessageSchema),
})
export type InvestorQuestion = z.infer<typeof investorQuestionSchema>

const personSchema = z.object({ id: z.string(), name: z.string() })

export const staffQuestionMessageSchema = z.object({
  id: z.string(),
  author: personSchema,
  author_role: z.enum(['investor', 'manager']),
  body: z.string(),
  created_at: z.string(),
})
export type StaffQuestionMessage = z.infer<typeof staffQuestionMessageSchema>

export const staffQuestionSchema = z.object({
  id: z.string(),
  subject: z.string(),
  status: z.enum(QUESTION_STATUSES.values),
  org: z.object({ id: z.string(), name: z.string() }),
  engagement: engagementRefSchema.extend({ manager_id: z.string() }),
  asked_by: personSchema,
  message_count: z.number().int(),
  last_message_at: z.string(),
  created_at: z.string(),
  can_reply: z.boolean(),
  messages: z.array(staffQuestionMessageSchema),
})
export type StaffQuestion = z.infer<typeof staffQuestionSchema>
